import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { 
  FiBook, 
  FiCalendar, 
  FiTrendingUp,
  FiAward,
  FiBarChart2,
  FiPrinter,
  FiInfo
} from 'react-icons/fi';
import './KHSMahasiswa.css';

// Grade points mapping (constant)
const GRADE_POINTS = {
  'A': 4.0,
  'A-': 3.7,
  'B+': 3.3,
  'B': 3.0,
  'B-': 2.7,
  'C+': 2.3,
  'C': 2.0,
  'C-': 1.7,
  'D': 1.0,
  'E': 0.0
};

const KHSMahasiswa = () => {
  const { user } = useAuth();
  const [khsList, setKhsList] = useState([]);
  const [loading, setLoading] = useState([]);
  const [selectedSemester, setSelectedSemester] = useState('');
  const [availableSemesters, setAvailableSemesters] = useState([]);
  const [stats, setStats] = useState({
    totalMatakuliah: 0,
    totalSKS: 0,
    ips: 0,
    ipk: 0
  });

  const fetchKHSList = useCallback(async () => {
    if (!selectedSemester) return;
    
    setLoading(true);
    try {
      const res = await axios.get('/api/khs', { 
        params: { 
          nim: user.nim,
          semester: selectedSemester,
          limit: 1000 
        } 
      });
      
      const data = res.data.data || [];
      setKhsList(data);
      
      // Calculate IPS
      let totalMutu = 0;
      let totalSKS = 0;
      
      data.forEach(khs => {
        const sks = khs.sks_mata_kuliah || 0;
        const gradePoint = GRADE_POINTS[khs.nilai_huruf] || 0;
        totalSKS += sks;
        totalMutu += sks * gradePoint;
      });
      
      const ips = totalSKS > 0 ? totalMutu / totalSKS : 0;
      
      setStats({
        totalMatakuliah: data.length,
        totalSKS,
        ips: parseFloat(ips.toFixed(2)),
        ipk: 0 // Will be calculated from all semesters
      });
      
    } catch (error) {
      console.error('Error fetching KHS:', error);
      if (error.response?.status === 404) {
        setKhsList([]);
        setStats({ totalMatakuliah: 0, totalSKS: 0, ips: 0, ipk: 0 });
      } else {
        toast.error('Gagal memuat data KHS');
      }
    } finally {
      setLoading(false);
    }
  }, [selectedSemester, user.nim]);

  const fetchAvailableSemesters = useCallback(async () => {
    try {
      const res = await axios.get('/api/khs', { 
        params: { 
          nim: user.nim,
          limit: 1000 
        } 
      });
      
      const khsData = res.data.data || [];
      
      // Extract unique semesters
      const semesterMap = new Map();
      
      khsData.forEach(khs => {
        if (khs.id_semester && !semesterMap.has(khs.id_semester)) {
          semesterMap.set(khs.id_semester, {
            id_semester: khs.id_semester,
            nama_semester: khs.nama_semester || khs.id_semester
          });
        }
      });
      
      const semesters = Array.from(semesterMap.values())
        .sort((a, b) => b.id_semester.localeCompare(a.id_semester));
      
      setAvailableSemesters(semesters);
      if (semesters.length > 0) {
        setSelectedSemester(semesters[0].id_semester);
      }
    } catch (error) {
      console.error('Error fetching semesters:', error);
      toast.error('Gagal memuat daftar semester');
    }
  }, [user.nim]);

  useEffect(() => {
    fetchAvailableSemesters();
  }, [fetchAvailableSemesters]);

  useEffect(() => {
    if (selectedSemester) {
      fetchKHSList();
    }
  }, [selectedSemester, fetchKHSList]);

  const handlePrint = () => {
    if (!selectedSemester) {
      toast.warning('Pilih semester terlebih dahulu');
      return;
    }
    const printUrl = `http://localhost:5000/api/khs/print/${user.nim}/${selectedSemester}`;
    window.open(printUrl, '_blank');
  };

  const getSemesterName = (semesterId) => {
    const semester = availableSemesters.find(s => s.id_semester === semesterId);
    return semester?.nama_semester || semesterId;
  };

  const getGradeColor = (grade) => {
    if (!grade) return 'var(--text-secondary)';
    if (grade === 'A' || grade === 'A-') return '#10b981';
    if (grade === 'B+' || grade === 'B') return '#3b82f6';
    if (grade === 'B-' || grade === 'C+') return '#f59e0b';
    if (grade === 'C' || grade === 'C-') return '#ef4444';
    return '#dc2626';
  };

  const getIPSStatus = (ips) => {
    if (ips >= 3.5) return { text: 'Sangat Memuaskan', color: '#10b981' };
    if (ips >= 3.0) return { text: 'Memuaskan', color: '#3b82f6' };
    if (ips >= 2.5) return { text: 'Cukup', color: '#f59e0b' };
    return { text: 'Kurang', color: '#ef4444' };
  };

  const ipsStatus = getIPSStatus(stats.ips);

  return (
    <div className="khs-mahasiswa-container">
      {/* Header Section */}
      <div className="khs-header">
        <div className="header-backdrop"></div>
        <div className="header-content">
          <div className="header-left">
            <div className="icon-wrapper">
              <FiAward className="header-icon" />
            </div>
            <div>
              <h1 className="header-title">Kartu Hasil Studi (KHS)</h1>
              <p className="header-subtitle">Hasil evaluasi akademik per semester</p>
            </div>
          </div>
          <div className="header-actions">
            {khsList.length > 0 && (
              <button className="btn-action btn-print" onClick={handlePrint}>
                <FiPrinter />
                <span>Cetak KHS</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Semester Selector */}
      <div className="semester-selector-card">
        <div className="selector-header">
          <FiCalendar className="selector-icon" />
          <h3>Pilih Semester</h3>
        </div>
        <div className="selector-content">
          <select 
            value={selectedSemester} 
            onChange={(e) => setSelectedSemester(e.target.value)}
            className="semester-select"
          >
            <option value="">-- Pilih Semester --</option>
            {availableSemesters.map(sem => (
              <option key={sem.id_semester} value={sem.id_semester}>
                {getSemesterName(sem.id_semester)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {selectedSemester && (
        <>
          {/* Stats Cards */}
          <div className="stats-grid">
            <div className="stat-card gradient-blue">
              <div className="stat-icon">
                <FiBook />
              </div>
              <div className="stat-details">
                <p className="stat-label">Total Mata Kuliah</p>
                <h2 className="stat-value">{stats.totalMatakuliah}</h2>
              </div>
            </div>

            <div className="stat-card gradient-green">
              <div className="stat-icon">
                <FiBarChart2 />
              </div>
              <div className="stat-details">
                <p className="stat-label">Total SKS</p>
                <h2 className="stat-value">{stats.totalSKS}</h2>
              </div>
            </div>

            <div className="stat-card gradient-purple">
              <div className="stat-icon">
                <FiTrendingUp />
              </div>
              <div className="stat-details">
                <p className="stat-label">Indeks Prestasi Semester</p>
                <h2 className="stat-value">{stats.ips.toFixed(2)}</h2>
                <span 
                  className="stat-badge" 
                  style={{ background: ipsStatus.color }}
                >
                  {ipsStatus.text}
                </span>
              </div>
            </div>
          </div>

          {/* KHS Table */}
          <div className="khs-table-card">
            <div className="table-header">
              <h3>
                <FiBook />
                Daftar Nilai - {getSemesterName(selectedSemester)}
              </h3>
              <span className="table-count">{khsList.length} mata kuliah</span>
            </div>

            {loading ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Memuat data KHS...</p>
              </div>
            ) : khsList.length === 0 ? (
              <div className="empty-state">
                <FiInfo size={48} />
                <p>Belum ada data KHS untuk semester ini</p>
                <small>Nilai akan muncul setelah dosen menginput nilai</small>
              </div>
            ) : (
              <div className="table-container">
                <table className="modern-table">
                  <thead>
                    <tr>
                      <th width="50">No</th>
                      <th width="120">Kode MK</th>
                      <th>Mata Kuliah</th>
                      <th width="80">SKS</th>
                      <th width="120">Nilai Angka</th>
                      <th width="120">Nilai Huruf</th>
                      <th width="100">Mutu</th>
                    </tr>
                  </thead>
                  <tbody>
                    {khsList.map((khs, index) => {
                      const sks = khs.sks_mata_kuliah || 0;
                      const gradePoint = GRADE_POINTS[khs.nilai_huruf] || 0;
                      const mutu = sks * gradePoint;
                      
                      return (
                        <tr key={khs._id || index}>
                          <td className="text-center">{index + 1}</td>
                          <td>
                            <code className="code-badge">{khs.kode_mata_kuliah || '-'}</code>
                          </td>
                          <td>
                            <strong>{khs.nama_mata_kuliah || '-'}</strong>
                          </td>
                          <td className="text-center">
                            <span className="sks-badge">{sks}</span>
                          </td>
                          <td className="text-center">
                            {khs.nilai_angka || '-'}
                          </td>
                          <td className="text-center">
                            <span 
                              className="grade-badge" 
                              style={{ 
                                background: getGradeColor(khs.nilai_huruf),
                                color: 'white'
                              }}
                            >
                              {khs.nilai_huruf || '-'}
                            </span>
                          </td>
                          <td className="text-center">
                            <strong>{mutu.toFixed(2)}</strong>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="total-row">
                      <td colSpan="3" className="text-right">
                        <strong>TOTAL</strong>
                      </td>
                      <td className="text-center">
                        <strong>{stats.totalSKS}</strong>
                      </td>
                      <td colSpan="2"></td>
                      <td className="text-center">
                        <strong>
                          {khsList.reduce((sum, khs) => {
                            const sks = khs.sks_mata_kuliah || 0;
                            const gradePoint = GRADE_POINTS[khs.nilai_huruf] || 0;
                            return sum + (sks * gradePoint);
                          }, 0).toFixed(2)}
                        </strong>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>

          {/* Info Box */}
          <div className="info-box">
            <FiInfo />
            <div>
              <strong>Keterangan Nilai:</strong>
              <p>A = 4.0 | A- = 3.7 | B+ = 3.3 | B = 3.0 | B- = 2.7 | C+ = 2.3 | C = 2.0 | C- = 1.7 | D = 1.0 | E = 0.0</p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default KHSMahasiswa;
